import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import { useHistory } from 'react-router-dom'
import { FiAlertTriangle, FiArrowRight } from 'react-icons/fi'
import { useQuery } from '@tanstack/react-query'
import { detect } from 'detect-browser'
import Card from '../../../../components/Card'
import useQueryParams from '../../../../hooks/useQuery'
import { api, authHeaders } from '../../../../services/api'
import Spinner from '../../../../components/Spinner'
import { Button, Select } from '../../../../components/Form'
import { GlobalContext } from '../../../../contexts/GlobalContext'
import masks from '../../../../util/masks'
import scrollUtils from '../../../../util/scroll'
import mercadoPago from '../../../../assets/images/mercadopago.png'
import escudo from '../../../../assets/images/escudo.png'

const emailUsuarioTeste = 'test_user_1878552748@testuser.com'

let upgradeMessage = null

const browser = detect()
const isSafari = ['ios-webview', 'ios', 'safari'].includes(browser.name)

export default function () {
	const { modulo } = useQueryParams()

	const { user } = useContext(GlobalContext)

	const history = useHistory()

	const [paymentLoadingErrorMessage, setPaymentLoadingErrorMessage] = useState(null)
	const [planoSelecionado, setPlanoSelecionado] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [alteracaoPlanoSolicitada, setAlteracaoPlanoSolicitada] = useState(false)

	const { data: planos } = useQuery({
		queryKey: ['obter-planos-disponiveis'],
		queryFn: async () => {
			const response = await api.get('plano', {
				params: {
					modulo_id: modulo
				}
			})

			return response
		},
		enabled: !!modulo,
		refetchOnWindowFocus: false
	})

	const { data: planoAssinatura } = useQuery({
		queryKey: ['obter-assinatura-pessoaa'],
		queryFn: async () => {
			const response = await api.get('assinaturas_pessoa', {
				params: {
					status: 'authorized',
					modulo
				},
				...authHeaders()
			})

			return response
		},
		refetchOnWindowFocus: false
	})

	async function loadBricks() {
		const mp = new window.MercadoPago(process.env.REACT_APP_MERCADO_PAGO_KEY, {
			locale: 'pt-BR'
		})

		const bricksBuilder = mp.bricks()

		const email = process.env.REACT_APP_ENVIRONMENT === 'development' ?
			emailUsuarioTeste :
			user.pessoa.contato.find(c => c.tipo_contato_id === 6)?.contato

		async function renderCardPaymentBrick(bricksBuilder) {
			const settings = {
				initialization: {
					amount: planoSelecionado.valor / 100,
					payer: {
						email
					}
				},
				customization: {
					visual: {
						style: {
							theme: 'default'
						},
						texts: {
							formTitle: 'Cartão de crédito',
							formSubmit: 'Contratar'
						}
					},
					paymentMethods: {
						maxInstallments: 1,
						types: {
							excluded: ['debit_card']
						}
					}
				},
				callbacks: {
					onReady: async () => {
						setIsLoading(false)
					},
					onSubmit: async cardFormData => {
						try {
							await api.put('plano/alterar', {
								token: cardFormData.token,
								modulo: +modulo,
								plano: planoSelecionado.id
							}, authHeaders())

							toast.success('Seu plano foi alterado com sucesso!')

							history.push('/assinaturas')
						} catch (e) {
							toast.error(e.msg)
						}
					},
					onError: e => {
						toast.error(e.message)
					}
				}
			}

			window.cardPaymentBrickController = await bricksBuilder.create('cardPayment', 'cardPaymentBrick_container', settings)
		}

		await renderCardPaymentBrick(bricksBuilder)
	}

	useEffect(() => {
		if (alteracaoPlanoSolicitada) {
			async function exec() {
				try {
					await loadBricks()
				} catch (e) {
					let errorMessage = 'Ocorreu um erro na comunicação com o <span class="font-semibold">Mercado Pago</span>. Você pode tentar novamente em alguns minutos'

					if (isSafari) {
						errorMessage += ' ou tentar utilizar outro navegador (recomenda-se o Google Chrome)'
					}

					errorMessage += '.'

					setPaymentLoadingErrorMessage(errorMessage)
				}
			}

			exec()
		}

		return () => {
			window.cardPaymentBrickController?.unmount()
		}
	}, [alteracaoPlanoSolicitada])

	if (!user || !planos || !planoAssinatura || isLoading) {
		return <Spinner />
	}

	const planoAtual = planos.find(plano => plano.id === planoAssinatura.plano_id)

	const planosSelectOptions = planos
		.filter(plano => plano.id !== planoAtual.id)
		.filter(plano => plano.id !== 1)
		.map(plano => ({
			label: `${plano.descricao} ${plano.periodo}`,
			value: plano.id
		}))

	const ePlanoGratis = planoAtual?.id === 1

	return (
		<div className="flex flex-col text-slate-800 sm:mx-4 sm:my-6 md:my-12 min-h-[46vh] leading-loose">
			<Card className="flex flex-col">
				<h1 className="text-slate-800 mb-4">Alteração de plano</h1>

				<p
					className="mx-2"
					dangerouslySetInnerHTML={{
						__html: `Seu plano atual é: <span class="font-bold">${`${planoAtual.modulo.descricao} - ${planoAtual?.descricao} ${ePlanoGratis ? 'GRATUITO' : planoAtual?.periodo}`}</span>`
					}}
				/>

				<Formik
					initialValues={{
						plano: null
					}}
					onSubmit={() => {
						setAlteracaoPlanoSolicitada(true)

						setTimeout(() => {
							scrollUtils.toElement('#form-pagamento')
						}, 200)
					}}
				>
					{({ setFieldValue, values }) => {
						const planoSelecionado = planos.find(plano => plano.id === values.plano?.value)
						const listaRecursosAtual = planoAtual?.recursos.filter(rec => !rec.descricao.startsWith('Inclui todas'))
						const listaRecursosNovo = planoSelecionado?.recursos.filter(rec => !rec.descricao.startsWith('Inclui todas'))

						return (
							<Form className="flex flex-col gap-2">
								<Select
									name="plano"
									label="Escolha o plano"
									onChange={(selected, meta) => {
										setFieldValue(meta.name, selected)

										const plano = planos.find(p => p.id === selected.value)
										setPlanoSelecionado(plano)

										setAlteracaoPlanoSolicitada(false)

										window.cardPaymentBrickController?.unmount()
									}}
									options={planosSelectOptions}
								/>

								<div className="flex flex-col sm:flex-row gap-6 items-start items-stretch mt-3">
									{listaRecursosAtual && listaRecursosNovo && (
										<div className="my-5 mx-2 flex-1">
											<span className="bg-yellow-300 rounded-md p-2 w-fit text-xs">Seu plano atual</span>

											<h2 className="font-bold text-slate-800 mb-3 text-lg mt-2">
												Recursos do {planoAtual.descricao} {ePlanoGratis ? 'GRATUITO' : planoAtual.periodo}
											</h2>

											<ul className="ml-6">
												{listaRecursosAtual.map(rec => (
													<li
														key={rec.id}
														dangerouslySetInnerHTML={{
															__html: rec.descricao
														}}
													/>
												))}
											</ul>

											<p
												className="mt-5 mx-6"
												dangerouslySetInnerHTML={{
													__html: `Valor do plano: <span class="font-bold text-lg">${masks.maskApply.currency(planoAtual.valor / 100)} ${planoAtual.periodo === 'anual' ? '/ano' : '/mês'}</span>`
												}}
											/>
										</div>
									)}

									{listaRecursosNovo && (
										<>
											<div
												className="w-[1px] bg-slate-300 hidden sm:block"
											/>

											<div
												className="h-[1px] bg-slate-300 sm:hidden"
											/>
										</>
									)}

									{listaRecursosNovo && (
										<div className="my-5 mx-2 flex-1">
											<span className="bg-emerald-500 text-white rounded-md p-2 w-fit text-xs">Novo plano selecionado</span>

											<h2 className="font-bold text-slate-800 mb-3 text-lg mt-2">
												Recursos do {planoSelecionado.descricao} {planoSelecionado.periodo}
											</h2>

											<ul className="ml-6">
												{listaRecursosNovo.map(rec => (
													<li
														key={rec.id}
														dangerouslySetInnerHTML={{
															__html: rec.descricao
														}}
													/>
												))}
											</ul>

											<p
												className="mt-5 mx-6"
												dangerouslySetInnerHTML={{
													__html: `Valor do plano: <span class="font-bold text-lg">${masks.maskApply.currency(planoSelecionado.valor / 100)} ${planoSelecionado.periodo === 'anual' ? '/ano' : '/mês'}</span> ${planoSelecionado.periodo === 'mensal' ? `<span class="text-xs ml-4 text-slate-400">${masks.maskApply.currency((planoSelecionado.valor * 12) / 100)}/ano</span>` : ''}`
												}}
											/>
										</div>
									)}
								</div>

								{planoSelecionado && (
									<>
										<Button type="submit" className="self-center">
											Alterar meu plano
											<FiArrowRight size={16} />
										</Button>

										<p className="text-slate-400 mt-8 mx-2">
											Ao realizar a troca de plano, você concorda com nossa <a href="/politica-reembolso" target="_blank">Política de Cancelamento e Reembolso</a>.
										</p>
									</>
								)}
							</Form>
						)
					}}
				</Formik>

				{alteracaoPlanoSolicitada && (
					<div className="w-96 self-center mt-10" id="form-pagamento">
						<div className="flex gap-3 text-slate-800 items-center">
							<img src={mercadoPago} alt="" className="h-7" />

							<p>Pagamento seguro com Mercado Pago</p>
						</div>

						{isSafari && paymentLoadingErrorMessage && (
							<div className="p-3 rounded-md bg-yellow-400 w-96 mt-6 flex flex-col gap-2 items-center animated flash fast">
								<FiAlertTriangle size={22} />

								<div
									dangerouslySetInnerHTML={{
										__html: paymentLoadingErrorMessage
									}}
								/>
							</div>
						)}

						<h1 className="text-slate-800 font-bold mt-8" style={{ fontSize: 22 }}>Resumo da compra</h1>

						<div className="mt-2 w-96">Você está alterando seu plano para:</div>

						<div className="pb-4 w-96 flex flex-col gap-2">
							<span className="font-bold" style={{ fontSize: 18 }}>{planoSelecionado.modulo.descricao}</span>
							<span className="font-bold">{`${planoSelecionado.descricao} ${planoSelecionado.periodo} - ${masks.maskApply.currency(planoSelecionado.valor / 100)} / ${planoSelecionado.periodo === 'mensal' ? 'mês' : 'ano'}`}</span>
						</div>

						{upgradeMessage}

						<div id="cardPaymentBrick_container" className="mb-8 mt-4 w-96 shadow-md rounded-xl"></div>

						<div className="grid grid-cols-[5rem_auto] gap-2 max-w-96">
							<img src={escudo} alt="" className="h-14 justify-self-center" />

							<div className="text-sm">Todos os pagamentos passam por nosso <span className="font-semibold">Sistema Antifraude</span>, portanto a cobrança em seu cartão de crédito poderá levar até 2 horas para ser realizada.</div>
						</div>
					</div>
				)}
			</Card>
		</div>
	)
}
